import { useMemo, useState } from 'react';
import type { DailyAction, DailyReturnCard, DailyReturnV2 } from '../cloud/api';
import { ReactionIcon, reactionLabel } from './components/ReactionIcon';

type StackCard = DailyReturnCard | {
    kind: 'voice_setup';
    id: string;
    title: string;
    body: string;
    action: DailyAction;
};

export interface DailyReturnStackProps {
    palId: string;
    daily: DailyReturnV2;
    voiceSetup?: { visible: boolean; action: DailyAction };
    onAction: (action: DailyAction) => void;
}

export function DailyReturnStack({ palId, daily, voiceSetup, onAction }: DailyReturnStackProps) {
    const [dismissVersion, setDismissVersion] = useState(0);
    const cards = useMemo(() => {
        const out: StackCard[] = [...daily.cards];
        if (voiceSetup?.visible) {
            out.push({
                kind: 'voice_setup',
                id: 'local:voice-setup',
                title: 'Talk to Moji while driving',
                body: 'Set up local voice controls for the landscape Drive cockpit.',
                action: voiceSetup.action,
            });
        }
        return out;
    }, [daily.cards, voiceSetup]);

    const visible = cards.filter((card) => {
        if (card.kind === 'empty_state' && daily.cards.length === 1) return true;
        return !isCardDismissed(palId, card, daily.date, dismissVersion);
    });

    if (visible.length === 0) return null;

    return (
        <section className="card border border-success/50" aria-label="Today">
            <div className="flex items-start gap-3 mb-3">
                <div className="min-w-0 flex-1">
                    <div className="muted text-xs mb-1">today</div>
                    <h3 className="font-semibold mb-2">{daily.headline}</h3>
                    <p className="text-sm">{daily.body}</p>
                </div>
                {daily.primary_action && (
                    <button
                        type="button"
                        className="btn-primary shrink-0 text-sm"
                        onClick={() => onAction(daily.primary_action!)}
                    >
                        {daily.primary_action.label}
                    </button>
                )}
            </div>

            <div className="space-y-2">
                {visible.map((card) => (
                    <DailyReturnRow
                        key={`${card.kind}:${card.id}`}
                        card={card}
                        onAction={onAction}
                        onDismiss={() => {
                            writeDismissedCard(palId, card, daily.date);
                            setDismissVersion((v) => v + 1);
                        }}
                    />
                ))}
            </div>
        </section>
    );
}

function DailyReturnRow({ card, onAction, onDismiss }: {
    card: StackCard;
    onAction: (action: DailyAction) => void;
    onDismiss: () => void;
}) {
    const action = actionForCard(card);
    const dismissible = card.kind !== 'empty_state' && card.kind !== 'voice_setup';

    return (
        <div className="rounded-lg bg-surface-raised p-3 text-sm">
            <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                    <CardBody card={card} />
                </div>
                {action && (
                    <button
                        type="button"
                        className="btn-secondary shrink-0 text-xs"
                        onClick={() => onAction(action)}
                    >
                        {actionButtonLabel(action)}
                    </button>
                )}
                {dismissible && (
                    <button
                        type="button"
                        className="muted shrink-0 text-xs underline"
                        onClick={onDismiss}
                        aria-label={`Dismiss ${card.kind}`}
                    >
                        Done
                    </button>
                )}
            </div>
        </div>
    );
}

function CardBody({ card }: { card: StackCard }) {
    switch (card.kind) {
        case 'empty_state':
        case 'map_suggestion':
        case 'privacy_nudge':
        case 'voice_setup':
            return (
                <>
                    <div className="font-medium">{card.title}</div>
                    <p className="muted mt-1">{card.body}</p>
                </>
            );
        case 'activity_memory':
            return (
                <>
                    <div className="muted text-xs mb-1">{card.title}</div>
                    <p>{card.body}</p>
                </>
            );
        case 'collection_progress':
            return (
                <>
                    <div className="flex justify-between gap-3 text-xs mb-1">
                        <span className="font-medium">{card.title}</span>
                        <span className="muted shrink-0">{card.progress}/{card.target}</span>
                    </div>
                    <div className="h-2 bg-surface rounded-full overflow-hidden mb-2">
                        <div
                            className="h-full bg-brand"
                            style={{ width: `${progressPct(card.progress, card.target)}%` }}
                            aria-hidden
                        />
                    </div>
                    <p className="muted text-xs">{card.next_action}</p>
                </>
            );
        case 'quest_progress':
            return (
                <>
                    <div className="flex justify-between gap-3 text-xs mb-1">
                        <span className="font-medium">{card.title}</span>
                        <span className="muted shrink-0">{card.progress}/{card.of}</span>
                    </div>
                    <div className="h-2 bg-surface rounded-full overflow-hidden">
                        <div
                            className="h-full bg-success"
                            style={{ width: `${progressPct(card.progress, card.of)}%` }}
                            aria-hidden
                        />
                    </div>
                </>
            );
        case 'reaction_summary':
            return (
                <>
                    <div className="font-medium">
                        {card.total} reaction{card.total === 1 ? '' : 's'} on your Echoes
                    </div>
                    <p className="muted mt-1 flex flex-wrap gap-2">
                        {card.parts.map((part) => (
                            <span key={part.kind} className="inline-flex items-center gap-1">
                                <ReactionIcon kind={part.kind} className="h-3 w-3" />
                                <span>{part.count}</span>
                                <span>{reactionLabel(part.kind).toLowerCase()}</span>
                            </span>
                        ))}
                    </p>
                </>
            );
        case 'last_echo':
            return (
                <>
                    <div className="muted text-xs mb-1">Last Echo</div>
                    <p>{card.node_name}</p>
                    {card.qualitative.length > 0 && (
                        <p className="muted text-xs mt-1">{card.qualitative.join(' - ')}</p>
                    )}
                </>
            );
    }
}

function actionForCard(card: StackCard): DailyAction | null {
    switch (card.kind) {
        case 'empty_state':
        case 'privacy_nudge':
        case 'voice_setup':
            return card.action;
        case 'map_suggestion':
            return { kind: 'map', label: 'Open', node_id: card.node_id };
        case 'last_echo':
            return { kind: 'map', label: 'Open', node_id: card.node_id };
        default:
            return null;
    }
}

function actionButtonLabel(action: DailyAction): string {
    switch (action.kind) {
        case 'drive':
            return 'Start';
        case 'map':
            return 'Open';
        case 'ask_moji':
            return 'Ask';
        case 'settings':
            return 'Set up';
    }
}

export function dailyReturnDismissKey(
    palId: string,
    cardKind: string,
    cardId: string,
    date: string,
): string {
    return `mochi.dailyReturn.dismissed.${palId}.${cardKind}.${cardId}.${date}`;
}

function isCardDismissed(
    palId: string,
    card: StackCard,
    date: string,
    _dismissVersion: number,
): boolean {
    try {
        return window.localStorage.getItem(dailyReturnDismissKey(palId, card.kind, card.id, date)) === '1';
    } catch {
        return false;
    }
}

function writeDismissedCard(palId: string, card: StackCard, date: string): void {
    try {
        window.localStorage.setItem(dailyReturnDismissKey(palId, card.kind, card.id, date), '1');
    } catch {
        // Ignore private browsing / storage errors.
    }
}

function progressPct(progress: number, target: number): number {
    return Math.min(100, Math.max(0, Math.round((progress / Math.max(1, target)) * 100)));
}
